/**
 * Convites. Um código curto e um link — nada mais.
 *
 * O convite pertence a um CANAL (é para lá que quem entra vai cair), por isso
 * usa-se o canal aberto; se não houver, o primeiro de texto do servidor.
 */

import type { Invite, Snowflake } from "@greenvoice/shared";
import { useState } from "react";
import { api } from "../lib/api";
import { channelsOf } from "../store/selectors";
import { useStore } from "../store/store";
import { IconClose } from "./Icons";

const EXPIRY_OPTIONS = [
  { label: "30 minutos", seconds: 1_800 },
  { label: "24 horas", seconds: 86_400 },
  { label: "7 dias", seconds: 604_800 },
  { label: "Nunca", seconds: null },
] as const;

export function InvitePanel({ serverId, onClose }: { serverId: Snowflake; onClose: () => void }) {
  const state = useStore();
  const active = state.activeChannelId ? state.channels[state.activeChannelId] : undefined;
  const target =
    active && active.serverId === serverId ? active : channelsOf(state, serverId).find((c) => c.type === "TEXT");

  const [expiry, setExpiry] = useState<number | null>(86_400);
  const [invite, setInvite] = useState<Invite | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  async function generate() {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      setInvite(
        await api.channels.createInvite(target.id, {
          expiresInSeconds: expiry,
          maxUses: null,
        }),
      );
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const link = invite ? `${window.location.origin}/invite/${invite.code}` : "";

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      // Sem clipboard (http em rede local, permissão negada): o input está
      // seleccionável, o utilizador copia à mão. Falhar em silêncio é pior.
      setError("Copie o link manualmente — o navegador bloqueou a área de transferência.");
    }
  }

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label="Convidar pessoas">
      <div className="modal__panel">
        <header className="modal__head">
          <h2>Convidar para {state.servers[serverId]?.name}</h2>
          <button type="button" className="icon-button" onClick={onClose} title="Fechar">
            <IconClose size={18} />
          </button>
        </header>

        {target ? (
          <p className="modal__hint">
            Quem usar este convite entra em <strong>#{target.name}</strong>.
          </p>
        ) : (
          <p className="modal__hint">Este servidor não tem canais de texto para receber convidados.</p>
        )}

        <label className="field">
          <span>Expira em</span>
          <select
            value={expiry === null ? "never" : String(expiry)}
            onChange={(e) => setExpiry(e.target.value === "never" ? null : Number(e.target.value))}
          >
            {EXPIRY_OPTIONS.map((option) => (
              <option key={option.label} value={option.seconds === null ? "never" : option.seconds}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="button button--primary"
          disabled={busy || !target}
          onClick={() => void generate()}
        >
          {invite ? "Gerar outro" : "Gerar convite"}
        </button>

        {invite ? (
          <div className="invite__result">
            <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} />
            <button type="button" className="button" onClick={() => void copy()}>
              {copied ? "Copiado" : "Copiar"}
            </button>
          </div>
        ) : null}

        {error ? (
          <p className="auth__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
